import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import {
  AppLink,
  Button,
  Callout,
  LinkButton,
  ReadOnlyNote,
  SectionHeader,
  Skeleton,
  errorText,
  formatDateTime,
} from "@amber/ui";
import { budgetsQuery } from "../queries";
import { AllowancesPanel } from "./AllowancesPanel";
import { EditBudgetsDialog } from "./EditBudgetsDialog";
import { PolicyPanel } from "./PolicyPanel";
import { RevisionsPanel } from "./RevisionsPanel";
import { SpendPanel } from "./SpendPanel";
import { LimitsPanel, ThresholdsPanel } from "./ThresholdsAndLimits";

export function BudgetsSettings() {
  const query = useQuery(budgetsQuery);
  const [editing, setEditing] = useState(false);
  const data = query.data;
  const disabled = data && !data.actions.edit.enabled ? data.actions.edit.reason : null;
  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col overflow-auto">
      <SectionHeader
        titleId="section-title"
        title="Budgets and limits"
        lead="Monthly allowances, thresholds and limits that decide when optional work is suspended. Fresh monitoring of tracked people always comes first."
        meta={
          data ? (
            <>
              {data.last_changed ? (
                <span>
                  Last changed by <b>{data.last_changed.by}</b> · {formatDateTime(data.last_changed.at)}
                </span>
              ) : (
                <span>Initial values, not changed yet</span>
              )}
              <AppLink href="/settings/audit?action=operations" className="font-strong text-link">
                Audit log ({data.audit_count})
              </AppLink>
              {disabled ? <ReadOnlyNote>Read-only for your role · Admins can change this</ReadOnlyNote> : null}
            </>
          ) : null
        }
        actions={
          <Button
            variant="primary"
            disabledReason={data ? disabled : "Loading the current values"}
            onClick={() => {
              setEditing(true);
            }}
          >
            <Pencil aria-hidden="true" />
            Edit budgets and limits…
          </Button>
        }
      />
      {query.isError ? (
        <Callout
          tone="attention"
          role="alert"
          className="mx-4 max-compact:mx-2"
          actions={
            <LinkButton
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </LinkButton>
          }
        >
          Couldn&apos;t load budgets and limits. {errorText(query.error)}
        </Callout>
      ) : null}
      {query.isPending ? (
        <div className="mx-4 grid max-w-[1120px] gap-3.5 max-compact:mx-2" aria-hidden="true">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      ) : null}
      {data ? (
        <div className="mx-4 mb-4 grid max-w-[1120px] gap-3.5 max-compact:mx-2">
          {data.waiting_for_budget > 0 ? (
            <Callout tone="waiting" role="status">
              {data.waiting_for_budget === 1
                ? "1 item waits for Budget; it resumes when an allowance admits it."
                : `${String(data.waiting_for_budget)} items wait for Budget; they resume when an allowance admits them.`}
            </Callout>
          ) : null}
          <AllowancesPanel data={data} />
          <SpendPanel data={data} />
          <div className="grid grid-cols-2 gap-3.5 max-compact:grid-cols-1">
            <ThresholdsPanel data={data} />
            <LimitsPanel data={data} />
          </div>
          <PolicyPanel data={data} />
          <RevisionsPanel data={data} />
        </div>
      ) : null}
      {editing && data ? (
        <EditBudgetsDialog
          data={data}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </section>
  );
}
